import React, { useState, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../utils/useAuth/useAuth";
import { postAuth } from "../../../utils/api";
import s from "./OtpVerify.module.scss";

const OtpVerify = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const location = useLocation();
  const [otp, setOtp] = useState("");
  const [isOtpTouched, setIsOtpTouched] = useState(false);
  const [isLoginProcessing, setIsLoginProcessing] = useState(false);
  const [isResendProcessing, setIsResendProcessing] = useState(false);
  const [isOtpFormatWrong, setIsOtpFormatWrong] = useState(false);
  const [isOtpNotFound, setIsOtpNotFound] = useState(false);
  const [isOtpWrong, setIsOtpWrong] = useState(false);
  const [isOtpExpired, setIsOtpExpired] = useState(false);
  const [isServerError, setIsServerError] = useState(false);
  const [isOtpResend, setIsOtpResend] = useState(false);
  const [isOtpResendError, setIsOtpResendError] = useState(false);
  const { email, password } = location.state;

  useLayoutEffect(() => {
    localStorage.clear();
  }, []);

  const handleOtpTyping = (e) => {
    if (e.key === "Enter") {
      return;
    }
    const { value } = e.target;
    setOtp(value);
    setIsOtpTouched(true);
    setIsOtpFormatWrong(false);
    setIsOtpResend(false);
    setIsOtpNotFound(false);
    setIsOtpWrong(false);
    setIsServerError(false);
    setIsOtpResendError(false);
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    // validation: try to submit without otp
    if (!otp) {
      setIsOtpTouched(true);
      return;
    }
    // validation: otp should be 6 digits of number
    const regexRule = /^\d{6}$/g;
    if (!regexRule.test(otp)) {
      setIsOtpFormatWrong(true);
      return;
    }
    setIsOtpNotFound(false);
    setIsOtpWrong(false);
    setIsOtpExpired(false);
    setIsOtpResend(false);
    setIsServerError(false);
    setIsOtpResendError(false);
    setIsOtpTouched(true);
    setIsLoginProcessing(true);
    try {
      const response = await postAuth("/otp/verify", {
        email,
        password,
        otp,
        currentTime: Math.floor(Date.now() / 1000),
      });

      setIsLoginProcessing(false);
      if (!response || !response?.data) {
        setIsServerError(true);
        return;
      }
      if (response.status == 403) {
        setIsServerError(true);
        return;
      }
      const responseBody = response.data;
      switch (responseBody.responseCode) {
        case "0000": {
          login(responseBody.data);
          break;
        }
        case "4001": {
          // Send OTP failed
          setIsServerError(true);
          break;
        }
        case "4002": {
          // OTP not found
          setIsOtpNotFound(true);
          break;
        }
        case "4003": {
          // Failed to verify OTP
          setIsServerError(true);
          break;
        }
        case "4004": {
          // OTP not match
          setIsOtpWrong(true);
          break;
        }
        case "4005": {
          // OTP expired
          setIsOtpExpired(true);
          break;
        }
        default: {
          setIsServerError(true);
          break;
        }
      }
    } catch (error) {
      setIsServerError(true);
    }
  };

  const resendOtp = async (e) => {
    e.preventDefault();
    setIsOtpNotFound(false);
    setIsOtpWrong(false);
    setIsOtpExpired(false);
    setIsOtpResend(false);
    setIsResendProcessing(true);
    try {
      const apiResponse = await postAuth("/otp/send", {
        email,
        password,
      });
      setIsResendProcessing(false);
      if (apiResponse?.data?.responseCode === "0000") {
        setIsOtpResend(true);
      } else {
        setIsOtpResendError(true);
      }
      setIsOtpTouched(false);
    } catch (error) {
      setIsOtpResendError(true);
    }
  };

  const generateInputOtpHelperText = () => {
    if (isOtpTouched && !otp) {
      return t("page.otp.input.textHint");
    }
    if (isOtpTouched && isOtpFormatWrong) {
      return t("page.otp.input.textFormatWrong");
    }
    return " ";
  };

  const otpInputTextField = () => {
    return (
      <div className={s.input}>
        <TextField
          disabled={isLoginProcessing || isResendProcessing}
          label={t("page.otp.input.textLabel")}
          InputLabelProps={{ shrink: true }}
          onKeyUp={handleOtpTyping}
          placeholder={t("page.otp.input.placeholder")}
          helperText={generateInputOtpHelperText()}
          FormHelperTextProps={{ sx: { color: "red" } }}
          variant="standard"
          onKeyDown={(e) => {
            if (e.key == "Enter") {
              e.preventDefault();
              submitOtp(e);
            }
          }}
          sx={{
            "& .MuiInput-root": {
              height: "40px",
            },
          }}
        />
      </div>
    );
  };

  const loginButton = () => {
    let loginBtn;
    if (!isLoginProcessing) {
      loginBtn = t("page.otp.button.submit");
    } else {
      loginBtn = (
        <CircularProgress
          size={24}
          sx={{
            color: "#3f3518",
          }}
          style={{
            position: "absolute",
            top: "25%",
            left: "50%",
            marginLeft: "-12px",
          }}
        />
      );
    }
    return (
      <button
        disabled={isLoginProcessing || isResendProcessing}
        className={s.submitBtn}
        onClick={submitOtp}
      >
        {loginBtn}
      </button>
    );
  };

  const submitFailMessage = () => {
    let failMessage;
    if (isOtpWrong) {
      failMessage = t("page.otp.errorMessage.otpNotMatch");
    } else if (isOtpExpired) {
      failMessage = t("page.otp.errorMessage.otpExpired");
    } else if (isServerError) {
      failMessage = t("page.otp.errorMessage.serverError");
    } else if (isOtpNotFound) {
      failMessage = t("page.otp.errorMessage.otpNotFound");
    } else {
      failMessage = " ";
    }
    return <span className={s.submitFailMessage}>{failMessage}</span>;
  };

  const resendButton = () => {
    return (
      <div className={s.resendOtpBtnContainer}>
        <span className={s.resendOtpLabel}>{t("page.otp.labelResendOtp")}</span>
        <button
          disabled={isLoginProcessing || isResendProcessing}
          className={s.resendOtpBtn}
          onClick={resendOtp}
        >
          <span>{t("page.otp.button.resendOtp")}</span>
          {isResendProcessing && (
            <CircularProgress
              size={16}
              sx={{ color: "#222" }}
              style={{
                position: "absolute",
                marginTop: "6px",
                marginLeft: "75px",
              }}
            />
          )}
        </button>
      </div>
    );
  };

  const resendOtpMessage = () => {
    if (isOtpResend) {
      return (
        <span className={[s.resendOtpMessage, s.resendOtpSuccess].join(" ")}>
          {t("page.otp.hintMessage.resendOtp")}
        </span>
      );
    }
    if (isOtpResendError) {
      return (
        <span className={[s.resendOtpMessage, s.resendOtpFail].join(" ")}>
          {t("page.otp.errorMessage.resendFail")}
        </span>
      );
    }
    return <span className={s.resendOtpMessage}> </span>;
  };

  return (
    <div className={s.textField}>
      <p className={s.title}>{t("page.otp.title")}</p>
      <Box
        component="form"
        sx={{ "& .MuiTextField-root": { m: 0.5, width: 1 } }}
        autoComplete="off"
      >
        {otpInputTextField()}
        {loginButton()}
        {submitFailMessage()}
        {resendButton()}
        {resendOtpMessage()}
      </Box>
    </div>
  );
};

export default OtpVerify;
